import React from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import * as Sentry from '@sentry/react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { LazyMotion } from 'framer-motion';
import Router from 'Router';
import Spinner from 'components/Spinner';
import { TypescriptManager } from './PrintNow/TypescriptManagerClass';
import PrintSupport from './components/PrintSupport/index';
import { printtTheme } from './styles/theme/layout';
import i18n from './i18nconfig';
import { persistor, store } from './store/configureStore';
import { sentry_dsn } from './settings';
import { googleRedirectUri, takeGoogleCode } from './components/Auth/googleRedirect';
import { loginGoogle } from './store/features/user/userServices';
import { getTeam } from './store/features/team/teamServices';

Sentry.init({
  dsn: sentry_dsn,
  environment: `${process.env.REACT_APP_NODE_ENV}`,
  release: 'Printt@' + process.env.REACT_APP_NODE_ENV,
  integrations: [Sentry.browserTracingIntegration()],
  // Trace 10% of visits; tracing every page view and API call used up the Sentry quota (429s). Errors are still sent at 100%
  tracesSampleRate: 0.1,
});
declare global {
  interface Window {
    analytics: any;
    AppleID: any;
    typescriptManager: TypescriptManager;
  }
}

// Directly assign the new instance to window.typescriptManager
window.typescriptManager = new TypescriptManager();

// Back from signing in with Google on the whole page (phones): its code, taken off the address before the router runs
const googleCode = takeGoogleCode();

// Signs in with that code once the saved state has loaded, so the saved guest doesn't replace the signed-in user
const finishGoogleSignIn = () => {
  if (googleCode) {
    store.dispatch(loginGoogle({ code: googleCode, redirectUri: googleRedirectUri() })).then((res) => {
      if (res.meta.requestStatus === 'fulfilled') {
        store.dispatch(getTeam());
      }
    });
  }
};

// Animations use the small `m` components; their features load in their own chunk
const loadMotionFeatures = () => import('./utils/motionFeatures').then((module) => module.default);

const container = document.getElementById('root');
const root = createRoot(container!); // Create a root.

root.render(
  <ThemeProvider theme={printtTheme}>
    <CssBaseline />
    <LazyMotion features={loadMotionFeatures} strict>
      <I18nextProvider i18n={i18n}>
        <Provider store={store}>
          <PersistGate loading={<Spinner />} persistor={persistor} onBeforeLift={finishGoogleSignIn}>
            <PrintSupport>
              <Router />
            </PrintSupport>
          </PersistGate>
        </Provider>
      </I18nextProvider>
    </LazyMotion>
  </ThemeProvider>,
);
